// ENG-2277 (HTML Forms): what a Form's author should know before saving. It only informs: nothing
// here can block a save (AC 29).
import { cssLengthToInches } from "components/pdf-generation-v3/form-print.utils";
import {
  FORM_FONT_FAMILIES,
  mapSourceFontFamily
} from "components/pdf-generation-v3/pdf-fonts";
import { getImageSize } from "components/pdf-generation-v3/utils";
import {
  FORM_IMAGE_LOW_DPI,
  printDpi
} from "components/word-processor-field/form-image.utils";
import { surfacesMissingTag } from "components/word-processor-field/merge-tags/merge-tags-options/form-context-merge-tags.utils";

export type PdfCheck = {
  mergeTagCount: number;
  // Underscore lines and blank cells with no merge tag: they print empty, to be filled by hand.
  blanks: number;
  // Tags that fill only on some surfaces, with the ticked surfaces where they print blank.
  unfilledTags: { tag: string; surfaces: number[] }[];
  // Fonts with no metric-compatible match, which print in Lato.
  fontsPrintingDifferently: string[];
  lowDpiImages: number;
};

const MERGE_TAG = /\{\{\s*([^}(]+?)\s*(?:\([^)]*\))?\s*\}\}/g;
const BLANK_LINE = /_{3,}/;
const CSS_PX_PER_INCH = 96;
// Names that already print in Lato, so a reader would not call them "different".
const LATO_NAMES = new Set(["lato", "latoregular", "sans-serif"]);

const tagNames = (text: string) =>
  Array.from(
    new Set(Array.from(text.matchAll(MERGE_TAG), ([, name]) => name.trim()))
  );

const countBlanks = (body: HTMLElement) => {
  const blocks = Array.from(body.querySelectorAll("p, div, td, th, li")).filter(
    (block) => !block.querySelector("p, div, td, th, li")
  );
  const blankLines = blocks.filter(
    (block) =>
      BLANK_LINE.test(block.textContent ?? "") &&
      tagNames(block.textContent ?? "").length === 0
  ).length;
  const blankCells = Array.from(
    body.querySelectorAll("td[data-blank], th[data-blank]")
  ).filter(
    (cell) =>
      tagNames(cell.textContent ?? "").length === 0 &&
      !BLANK_LINE.test(cell.textContent ?? "")
  ).length;
  return blankLines + blankCells;
};

const firstFamilyName = (fontFamily: string) =>
  fontFamily
    .split(",")[0]
    .trim()
    .replace(/^["']|["']$/g, "")
    .trim();

const fontsPrintingDifferently = (body: HTMLElement) => {
  const names = Array.from(
    body.querySelectorAll<HTMLElement>('[style*="font-family"]')
  )
    .map((element) => element.style.fontFamily)
    .filter(
      (fontFamily) =>
        mapSourceFontFamily(fontFamily) === FORM_FONT_FAMILIES.lato &&
        !LATO_NAMES.has(firstFamilyName(fontFamily).toLowerCase())
    )
    .map(firstFamilyName);
  return Array.from(new Set(names));
};

const printedWidthInches = (image: HTMLImageElement, naturalWidth: number) =>
  cssLengthToInches(image.style.width) ??
  (Number(image.getAttribute("width")) || naturalWidth) / CSS_PX_PER_INCH;

const countLowDpiImages = (body: HTMLElement) =>
  Array.from(
    body.querySelectorAll<HTMLImageElement>('img[src^="data:image/"]')
  ).filter((image) => {
    const size = getImageSize(image.getAttribute("src") ?? "");
    if (!size?.width) return false;
    return (
      printDpi(size.width, printedWidthInches(image, size.width)) <
      FORM_IMAGE_LOW_DPI
    );
  }).length;

export const pdfCheck = (html: string, placements: number[]): PdfCheck => {
  const { body } = new DOMParser().parseFromString(html, "text/html");
  const tags = tagNames(body.textContent ?? "");
  return {
    mergeTagCount: tags.length,
    blanks: countBlanks(body),
    unfilledTags: tags
      .map((tag) => ({ tag, surfaces: surfacesMissingTag(tag, placements) }))
      .filter(({ surfaces }) => surfaces.length > 0),
    fontsPrintingDifferently: fontsPrintingDifferently(body),
    lowDpiImages: countLowDpiImages(body)
  };
};

/** Pages in a PDF react-pdf wrote: its page objects, not the page tree (`/Type /Pages`). */
export const countPdfPages = (pdfText: string) =>
  (pdfText.match(/\/Type\s*\/Page(?![a-zA-Z])/g) ?? []).length;
